// ---------------------------------------------------------------------------
// Sprint 4 A1: Inbox Routing Rules
// ---------------------------------------------------------------------------
// Auto-assign inbound conversations based on configurable conditions.
// Evaluated in priority order (lower number = higher priority).

import { pgTable, uuid, text, integer, boolean, jsonb, timestamp, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export type RoutingConditionType =
  | "channel"
  | "keyword"
  | "segment"
  | "tag"
  | "business_hours"
  | "skill"
  | "language"
  | "availability";

export type RoutingOperator = "eq" | "neq" | "contains" | "in";

export type RoutingMemberAvailability = "idle" | "active" | "busy" | "offline";

export interface RoutingTarget {
  userId: string;
  label?: string;
  skills?: string[];
  languages?: string[];
}

/** Condition types for routing rules */
export interface RoutingCondition {
  type: RoutingConditionType;
  operator: RoutingOperator;
  value: unknown;
}

/** Action types for routing rules */
export interface RoutingAction {
  type: "assign_user" | "assign_team" | "round_robin" | "auto_tag";
  userId?: string;
  teamId?: string;
  userPool?: string[]; // for round_robin
  targets?: RoutingTarget[];
  tagIds?: string[];
}

export const inboxRoutingRules = pgTable(
  "inbox_routing_rules",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    priority: integer("priority").notNull().default(100), // lower = higher priority
    isActive: boolean("is_active").default(true).notNull(),
    conditions: jsonb("conditions").$type<RoutingCondition[]>().notNull().default([]),
    action: jsonb("action").$type<RoutingAction>().notNull(),
    matchCount: integer("match_count").default(0).notNull(), // analytics
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_routing_rules_tenant").on(table.tenantId),
    index("idx_routing_rules_priority").on(table.tenantId, table.priority),
  ],
);
